import React from 'react';
import { Translator } from '../../Translator';
import RadioButton from 'components/RadioButton';
import InputGroup from 'components/LoginSignUp/InputGroup';
import RoundedButton from 'components/RoundedButton';
import PasswordInput from 'components/PasswordInput';
import classnames from 'classnames';

const CSS = require('./SignUpForm.less');

interface SignUpFormProps {
  onSubmit: (e: any) => void;
  onChange: (e: any) => void;
  onPhoneCountryCodeClick: (e: any) => void;
  countries: any[];
  signupButtonLoading: boolean;
  email: string;
  password: string;
  gender: undefined | 'man' | 'woman' | 'other';
  firstname: string;
  lastname: string;
  country: string;
  phoneCountryCode: string;
  phoneNumber: string;
  options: {
    gender?: boolean;
    firstname?: boolean;
    lastname?: boolean;
    country?: boolean;
    phoneCountryCode?: boolean;
    phoneNumber?: boolean;
  };
}

const renderCountries = (countries) => {
  return countries
    .map((country) => {
      return (<option key={country.countryCode} value={country.name}>{country.name}</option>);
    });
};

const renderCountriesByCallingCode = (countries, phoneCountryCode) => {
  return countries
    .concat()
    .sort((a, b) => {
      if (a.countryCode === 'US') {
        return -1;
      }
      return Number(a.callingCode) - Number(b.callingCode);
    })
    .filter((country, index, self) => !!country.callingCode && !!country.name && self.findIndex(r => r.name === country.name) === index)
    .map((country) => {
      const value = `${country.callingCode}_${country.name}`;
      const [countryCode, countryName] = phoneCountryCode.split('_');
      if (countryCode === country.callingCode && ((!countryName && country.countryCode === 'US') || countryName === country.name)) {
        return (
          <option key={value} value={value}>
            {`+${country.callingCode}`}
          </option>
        )
      }

      return (
        <option key={value} value={value}>
          {`${country.name} +${country.callingCode}`}
        </option>
      );
    });
};

const renderGenderOptions = (onClick, currentGender) => {
  return [
    (
      <label key={'man'}>
        <RadioButton
          checked={currentGender === 'man'}
          value={'man'}
          name={'gender'}
          onClick={onClick}
          required
        />
        {Translator.translate('Forms.Gender.male')}
      </label>
    ),
    (
      <label key={'woman'}>
        <RadioButton
          checked={currentGender === 'woman'}
          value={'woman'}
          name={'gender'}
          onClick={onClick}
          required
        />
        {Translator.translate('Forms.Gender.female')}
      </label>
    ),
    (
      <label key={'other'}>
        <RadioButton
          checked={currentGender === 'other'}
          value={'other'}
          name={'gender'}
          onClick={onClick}
          required
        />
        {Translator.translate('Forms.Gender.other')}
      </label>
    ),
  ];
};

function SignUpForm({ email, country, phoneCountryCode, phoneNumber, firstname, lastname, gender, password, options, countries, signupButtonLoading, onSubmit, onChange, onPhoneCountryCodeClick }: SignUpFormProps) {
  return (
    <form className={CSS.form} onSubmit={onSubmit}>
      {options.gender &&
        <InputGroup>
          <label className={CSS.inputLabel}>
            {Translator.translate('Forms.Labels.gender', {}, true, 'Gender')}
          </label>
          <div className={CSS.radioButtons}>
            {renderGenderOptions(onChange, gender)}
          </div>
        </InputGroup>
      }
      {options.firstname &&
        <InputGroup>
          <label className={CSS.inputLabel}>
            {Translator.translate('Forms.Labels.firstName', {}, true, 'First name')}
          </label>
          <input
            autoComplete={'given-name'}
            className={CSS.input}
            type={'text'}
            value={firstname}
            name={'firstname'}
            onChange={onChange}
            required
          />
        </InputGroup>
      }
      {options.lastname &&
        <InputGroup>
          <label className={CSS.inputLabel}>
            {Translator.translate('Forms.Labels.lastName', {}, true, 'Last name')}
          </label>
          <input
            autoComplete={'family-name'}
            className={CSS.input}
            type={'text'}
            value={lastname}
            name={'lastname'}
            onChange={onChange}
            required
          />
        </InputGroup>
      }
      <InputGroup>
        <label className={CSS.inputLabel}>
          {Translator.translate('Forms.Labels.email', {}, true, 'Email')}
        </label>
        <input
          autoComplete={'email'}
          className={CSS.input}
          type={'email'}
          value={email}
          name={'email'}
          onChange={onChange}
          required
        />
      </InputGroup>
      <InputGroup>
        <label className={CSS.inputLabel}>
          {Translator.translate('Forms.Labels.password', {}, true, 'Password')}
        </label>
        <PasswordInput value={password} handleChange={onChange}/>
      </InputGroup>
      {options.country &&
        <InputGroup>
          <label className={CSS.inputLabel}>
            {Translator.translate('Forms.Labels.country', {}, true, 'Country')}
          </label>
          <select
            className={CSS.input}
            name={'country'}
            value={country}
            onChange={onChange}
            required
          >
            <option value=""/>
            {renderCountries(countries)}
          </select>
        </InputGroup>
      }
      {options.phoneNumber &&
        <InputGroup>
          <label className={CSS.inputLabel}>
            {Translator.translate('Forms.Labels.phone', {}, true, 'Phone')}
          </label>
          <div className={CSS.phoneInput}>
            <select
              autoComplete={'tel-country-code'}
              className={classnames(CSS.input, CSS.countryCode)}
              name={'phoneCountryCode'}
              value={phoneCountryCode}
              onChange={onChange}
              onClick={onPhoneCountryCodeClick}
              required
            >
              {renderCountriesByCallingCode(countries, phoneCountryCode)}
            </select>
            <input
              autoComplete={'tel-national'}
              className={classnames(CSS.input, CSS.phoneNumberInput)}
              type={'phone'}
              value={phoneNumber}
              name={'phoneNumber'}
              onChange={onChange}
              required
            />
          </div>
        </InputGroup>
      }
      <RoundedButton
        type="submit"
        fullWidth
        className={CSS.signupButton}
        isLoading={signupButtonLoading}
      >
        {Translator.translate('Forms.Buttons.signUp', {}, true, 'Sign up')}
      </RoundedButton>
    </form>
  );
}

export default SignUpForm;
